import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import PopupOverlay from '../../organisms/Background/PopupOverlay';
import CloseButton from '../../atoms/CloseButton';
import { eventService } from '../../../services/eventService';
import useLocaleDateFormatter from '../../../hooks/useLocaleDateFormatter';
import {
    isCanonicalObjectId,
    mergeAttendanceState,
    normalizeScheduledEntityDetails,
    normalizeScheduledObjectType,
    resolveActiveRsvpStatus,
} from '../../../utils/scheduledEntity';
import './index.scss';

const RSVP_BUTTONS = Object.freeze([
    { status: 'GOING', translationKey: 'home.postCard.relatedObject.actions.going' },
    { status: 'INTERESTED', translationKey: 'home.postCard.relatedObject.actions.interested' },
    { status: 'DECLINED', translationKey: 'home.postCard.relatedObject.actions.declined' },
]);

function resolveStatusTranslationKey(status) {
    const normalized = typeof status === 'string' ? status.trim().toLowerCase() : '';
    return `home.postCard.relatedObject.status.${normalized || 'unknown'}`;
}

function resolveTypeLabelKey(objectType) {
    return objectType === 'SESSION'
        ? 'home.postCard.relatedObject.type.session'
        : 'home.postCard.relatedObject.type.event';
}

function formatJoinPolicyLabel(t, joinPolicy) {
    if (!joinPolicy) {
        return t('home.postCard.relatedObject.joinPolicy.open', 'Open');
    }
    if (joinPolicy === 'APPROVAL_REQUIRED') {
        return t('home.postCard.relatedObject.joinPolicy.approvalRequired', 'Approval required');
    }
    if (joinPolicy === 'CLOSED') {
        return t('home.postCard.relatedObject.joinPolicy.closed', 'Closed');
    }
    return t('home.postCard.relatedObject.joinPolicy.open', 'Open');
}

function formatViewerStatusLabel(t, status) {
    if (!status) {
        return t('home.postCard.relatedObject.viewerStatus.none', 'No RSVP yet');
    }
    return t(`home.postCard.relatedObject.viewerStatus.${status.toLowerCase()}`, status);
}

function resolveErrorStatus(error) {
    if (typeof error?.status === 'number') {
        return error.status;
    }
    if (typeof error?.response?.status === 'number') {
        return error.response.status;
    }
    return null;
}

function buildAddress(detail) {
    if (!detail) {
        return '';
    }
    const parts = [
        detail.locationName,
        detail.addressLine,
        detail.city,
        detail.stateOrRegion,
        detail.country,
    ].filter(Boolean);
    return parts.join(', ');
}

function resolveStatusVariant(status) {
    const normalized = typeof status === 'string' ? status.trim().toUpperCase() : '';
    if (normalized === 'PUBLISHED') return 'publish';
    if (normalized === 'CANCELLED') return 'cancel';
    if (normalized === 'COMPLETED') return 'complete';
    return 'publish';
}

function hasEditableOrganizerDetails(details) {
    const organizerType = typeof details?.organizer?.objectType === 'string'
        ? details.organizer.objectType.trim().toUpperCase()
        : '';
    return (organizerType === 'USER' || organizerType === 'BAND')
        && isCanonicalObjectId(details?.organizer?.objectId);
}

function isSameScheduledEntity(left, right) {
    return Boolean(left?.id && right?.id)
        && String(left.id) === String(right.id)
        && left.objectType === right.objectType;
}

function pickDetailField(currentDetails, summaryFallback, key, preferSummary) {
    const preferred = preferSummary ? summaryFallback?.[key] : currentDetails?.[key];
    const fallback = preferSummary ? currentDetails?.[key] : summaryFallback?.[key];
    return preferred ?? fallback;
}

function pickNullableDetailReference(currentDetails, summaryFallback, key, preferSummary) {
    if (preferSummary) {
        if (summaryFallback && Object.prototype.hasOwnProperty.call(summaryFallback, key)) {
            return summaryFallback[key] ?? null;
        }
        return currentDetails?.[key] ?? null;
    }
    if (currentDetails && Object.prototype.hasOwnProperty.call(currentDetails, key)) {
        return currentDetails[key] ?? null;
    }
    return summaryFallback?.[key] ?? null;
}

function mergeSummaryFallbackIntoDetails(currentDetails, summaryFallback, { preferSummary = false } = {}) {
    if (!summaryFallback) {
        return null;
    }
    if (!isSameScheduledEntity(currentDetails, summaryFallback)) {
        return summaryFallback;
    }

    return {
        ...currentDetails,
        title: pickDetailField(currentDetails, summaryFallback, 'title', preferSummary),
        description: pickDetailField(currentDetails, summaryFallback, 'description', preferSummary),
        status: pickDetailField(currentDetails, summaryFallback, 'status', preferSummary),
        visibility: pickDetailField(currentDetails, summaryFallback, 'visibility', preferSummary),
        timezone: pickDetailField(currentDetails, summaryFallback, 'timezone', preferSummary),
        startAt: pickDetailField(currentDetails, summaryFallback, 'startAt', preferSummary),
        endAt: pickDetailField(currentDetails, summaryFallback, 'endAt', preferSummary),
        joinPolicy: pickDetailField(currentDetails, summaryFallback, 'joinPolicy', preferSummary),
        attendeeLimit: pickDetailField(currentDetails, summaryFallback, 'attendeeLimit', preferSummary),
        organizer: pickNullableDetailReference(currentDetails, summaryFallback, 'organizer', preferSummary),
        venue: pickNullableDetailReference(currentDetails, summaryFallback, 'venue', preferSummary),
        locationName: pickDetailField(currentDetails, summaryFallback, 'locationName', preferSummary),
        addressLine: pickDetailField(currentDetails, summaryFallback, 'addressLine', preferSummary),
        city: pickDetailField(currentDetails, summaryFallback, 'city', preferSummary),
        stateOrRegion: pickDetailField(currentDetails, summaryFallback, 'stateOrRegion', preferSummary),
        country: pickDetailField(currentDetails, summaryFallback, 'country', preferSummary),
        countryCode: pickDetailField(currentDetails, summaryFallback, 'countryCode', preferSummary),
        latitude: pickDetailField(currentDetails, summaryFallback, 'latitude', preferSummary),
        longitude: pickDetailField(currentDetails, summaryFallback, 'longitude', preferSummary),
        attendanceSummary: pickNullableDetailReference(currentDetails, summaryFallback, 'attendanceSummary', preferSummary),
        viewerAttendanceStatus: pickDetailField(currentDetails, summaryFallback, 'viewerAttendanceStatus', preferSummary),
        updatedAt: pickDetailField(currentDetails, summaryFallback, 'updatedAt', preferSummary),
        createdAt: pickDetailField(currentDetails, summaryFallback, 'createdAt', preferSummary),
        viewerInvitedByOrganizer: preferSummary
            ? summaryFallback.viewerInvitedByOrganizer === true || currentDetails.viewerInvitedByOrganizer === true
            : currentDetails.viewerInvitedByOrganizer,
        organizerInviteCount: preferSummary
            ? summaryFallback.organizerInviteCount ?? currentDetails.organizerInviteCount
            : currentDetails.organizerInviteCount,
    };
}

function createDetailState(value, source) {
    return {
        source,
        value,
    };
}

const ICONS = {
    calendar: (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="5" width="18" height="16" rx="2" />
            <path d="M3 10h18M8 3v4M16 3v4" />
        </svg>
    ),
    pin: (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 21s7-7.5 7-12a7 7 0 1 0-14 0c0 4.5 7 12 7 12z" />
            <circle cx="12" cy="9" r="2.5" />
        </svg>
    ),
    lock: (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
            <path d="M8 10.5V8a4 4 0 1 1 8 0v2.5" />
        </svg>
    ),
    users: (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="9" cy="9" r="3.5" />
            <path d="M2.5 20c0-3.3 2.9-6 6.5-6s6.5 2.7 6.5 6" />
            <path d="M16 4.5a3.5 3.5 0 1 1 0 7" />
            <path d="M18 20c0-2.4 1.3-4.5 3.5-5.4" />
        </svg>
    ),
};

export default function ScheduledEntityDetailModal({
    isOpen = false,
    objectType = null,
    objectId = null,
    initialSummary = null,
    initialAttendance = null,
    onClose = null,
    onAttendanceChange = null,
    canEdit = false,
    onEdit = null,
    readOnly = false,
}) {
    const { t } = useTranslation();
    const { formatDateTime } = useLocaleDateFormatter();
    const normalizedObjectType = normalizeScheduledObjectType(objectType);

    const summaryFallback = useMemo(() => {
        const location = initialSummary?.location && typeof initialSummary.location === 'object'
            ? initialSummary.location
            : {};

        return normalizeScheduledEntityDetails(
            {
                id: objectId,
                objectType: normalizedObjectType,
                title: initialSummary?.title,
                description: initialSummary?.description,
                status: initialSummary?.status,
                visibility: initialSummary?.visibility,
                timezone: initialSummary?.timezone,
                startAt: initialSummary?.startAt,
                endAt: initialSummary?.endAt,
                joinPolicy: initialSummary?.joinPolicy,
                attendeeLimit: initialSummary?.attendeeLimit,
                organizerInviteCount: initialSummary?.organizerInviteCount,
                viewerInvitedByOrganizer: initialSummary?.viewerInvitedByOrganizer,
                attendanceSummary: initialSummary?.attendanceSummary,
                viewerAttendanceStatus: initialSummary?.viewerAttendanceStatus,
                organizer: initialSummary?.organizer,
                venue: initialSummary?.venue,
                updatedAt: initialSummary?.updatedAt,
                createdAt: initialSummary?.createdAt,
                location: {
                    locationName: initialSummary?.locationName ?? location.locationName,
                    addressLine: initialSummary?.addressLine ?? location.addressLine,
                    city: initialSummary?.city ?? location.city,
                    stateOrRegion: initialSummary?.stateOrRegion ?? location.stateOrRegion,
                    country: initialSummary?.country ?? location.country,
                    countryCode: initialSummary?.countryCode ?? location.countryCode,
                    latitude: initialSummary?.latitude ?? location.latitude,
                    longitude: initialSummary?.longitude ?? location.longitude,
                },
            },
            { objectType: normalizedObjectType, objectId },
        );
    }, [initialSummary, normalizedObjectType, objectId]);

    const [detailState, setDetailState] = useState(() => createDetailState(
        summaryFallback,
        summaryFallback ? 'summary' : 'empty',
    ));
    const details = detailState.value;
    const [attendance, setAttendance] = useState(() => mergeAttendanceState(
        initialAttendance,
        details,
        {
            id: objectId,
            objectType: normalizedObjectType,
        },
    ));
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const latestInitialAttendanceRef = useRef(initialAttendance);
    const latestOnAttendanceChangeRef = useRef(onAttendanceChange);
    const latestSummaryFallbackRef = useRef(summaryFallback);
    const activeDetails = useMemo(
        () => mergeSummaryFallbackIntoDetails(
            details,
            summaryFallback,
            { preferSummary: detailState.source !== 'detail' },
        ),
        [detailState.source, details, summaryFallback],
    );

    useLayoutEffect(() => {
        latestInitialAttendanceRef.current = initialAttendance;
    }, [initialAttendance]);

    useLayoutEffect(() => {
        latestOnAttendanceChangeRef.current = onAttendanceChange;
    }, [onAttendanceChange]);

    useLayoutEffect(() => {
        latestSummaryFallbackRef.current = summaryFallback;
    }, [summaryFallback]);

    useEffect(() => {
        setDetailState((currentState) => {
            const isSameEntity = isSameScheduledEntity(currentState.value, summaryFallback);
            const nextValue = mergeSummaryFallbackIntoDetails(
                currentState.value,
                summaryFallback,
                { preferSummary: currentState.source !== 'detail' },
            );
            const nextSource = summaryFallback && currentState.source === 'detail' && isSameEntity
                ? 'detail'
                : summaryFallback
                    ? 'summary'
                    : 'empty';

            return createDetailState(nextValue, nextSource);
        });
    }, [summaryFallback]);

    useEffect(() => {
        if (!normalizedObjectType || !objectId) {
            setAttendance(null);
            return;
        }

        setAttendance((current) => mergeAttendanceState(
            initialAttendance,
            isSameScheduledEntity(current, { id: objectId, objectType: normalizedObjectType })
                ? current
                : null,
            activeDetails,
            {
                id: objectId,
                objectType: normalizedObjectType,
            },
        ));
    }, [activeDetails, initialAttendance, normalizedObjectType, objectId]);

    useEffect(() => {
        if (!isOpen || !normalizedObjectType || !objectId || readOnly) {
            return undefined;
        }
        const abortController = new AbortController();
        let isMounted = true;

        const load = async () => {
            setIsLoading(true);
            setErrorMessage('');

            const [detailResult, attendanceResult] = await Promise.allSettled([
                eventService.getScheduledEntity(normalizedObjectType, objectId, { signal: abortController.signal }),
                eventService.getAttendance(normalizedObjectType, objectId, { signal: abortController.signal }),
            ]);

            if (!isMounted) {
                return;
            }

            const fallbackDetails = latestSummaryFallbackRef.current;
            const nextDetails = detailResult.status === 'fulfilled'
                ? normalizeScheduledEntityDetails(
                    detailResult.value,
                    {
                        objectType: normalizedObjectType,
                        objectId,
                        title: fallbackDetails?.title,
                        status: fallbackDetails?.status,
                    },
                )
                : null;

            const nextAttendance = mergeAttendanceState(
                attendanceResult.status === 'fulfilled'
                    ? attendanceResult.value
                    : null,
                nextDetails,
                latestInitialAttendanceRef.current,
                {
                    id: objectId,
                    objectType: normalizedObjectType,
                    status: nextDetails?.status ?? fallbackDetails?.status,
                },
            );

            setDetailState(createDetailState(
                nextDetails || fallbackDetails,
                nextDetails ? 'detail' : fallbackDetails ? 'summary' : 'empty',
            ));
            setAttendance(nextAttendance);

            const latestOnAttendanceChange = latestOnAttendanceChangeRef.current;
            if (nextAttendance && typeof latestOnAttendanceChange === 'function') {
                latestOnAttendanceChange(nextAttendance);
            }

            if (detailResult.status === 'rejected' && !nextDetails) {
                setErrorMessage(t(
                    'home.postCard.relatedObject.detail.loadError',
                    'We could not load event details right now.',
                ));
            }
            setIsLoading(false);
        };

        load().catch((error) => {
            if (!isMounted || error?.name === 'AbortError') {
                return;
            }
            setErrorMessage(t(
                'home.postCard.relatedObject.detail.loadError',
                'We could not load event details right now.',
            ));
            setIsLoading(false);
        });

        return () => {
            isMounted = false;
            abortController.abort();
        };
    }, [
        isOpen,
        normalizedObjectType,
        objectId,
        readOnly,
        t,
    ]);

    const activeAttendance = useMemo(() => (
        isSameScheduledEntity(attendance, { id: objectId, objectType: normalizedObjectType })
            ? attendance
            : null
    ), [attendance, normalizedObjectType, objectId]);

    const canMutateAttendance = useMemo(() => {
        const sourceStatus = activeAttendance?.scheduledStatus ?? activeDetails?.status ?? null;
        return !readOnly && sourceStatus === 'PUBLISHED';
    }, [activeAttendance?.scheduledStatus, activeDetails?.status, readOnly]);

    const attendanceSummary = activeAttendance?.attendanceSummary ?? activeDetails?.attendanceSummary ?? {
        going: 0,
        interested: 0,
        pending: 0,
    };

    const viewerStatus = activeAttendance?.viewerAttendanceStatus
        || activeDetails?.viewerAttendanceStatus
        || null;
    const activeViewerStatus = resolveActiveRsvpStatus(viewerStatus);
    const attendanceMergeFallback = useMemo(() => ({
        id: objectId,
        objectType: normalizedObjectType,
        scheduledStatus: activeDetails?.status,
        joinPolicy: activeDetails?.joinPolicy,
    }), [
        activeDetails?.joinPolicy,
        activeDetails?.status,
        normalizedObjectType,
        objectId,
    ]);

    const handleRsvp = useCallback(async (status) => {
        if (readOnly || !canMutateAttendance || isSubmitting || !normalizedObjectType || !objectId) {
            return;
        }
        if (activeViewerStatus === status) {
            return;
        }
        setIsSubmitting(true);
        setErrorMessage('');
        try {
            const payload = await eventService.upsertAttendance(normalizedObjectType, objectId, status);
            const next = mergeAttendanceState(
                payload,
                activeAttendance,
                activeDetails,
                attendanceMergeFallback,
            );
            if (!next) {
                throw new Error('Invalid attendance response');
            }
            setAttendance(next);
            if (typeof onAttendanceChange === 'function') {
                onAttendanceChange(next);
            }
        } catch (error) {
            const statusCode = resolveErrorStatus(error);
            setErrorMessage(
                statusCode === 409
                    ? t(
                        'home.postCard.relatedObject.detail.conflictError',
                        'RSVP updates are unavailable for this event right now.',
                    )
                    : t(
                        'home.postCard.relatedObject.detail.rsvpError',
                        'We could not update your RSVP. Please retry.',
                    ),
            );
        } finally {
            setIsSubmitting(false);
        }
    }, [
        activeAttendance,
        activeDetails,
        attendanceMergeFallback,
        canMutateAttendance,
        isSubmitting,
        normalizedObjectType,
        objectId,
        onAttendanceChange,
        readOnly,
        t,
        activeViewerStatus,
    ]);

    if (!isOpen || !normalizedObjectType || !objectId) {
        return null;
    }

    const title = activeDetails?.title || t('home.postCard.relatedObject.detail.untitled', 'Untitled event');
    const statusLabel = t(
        resolveStatusTranslationKey(activeDetails?.status),
        activeDetails?.status || t('home.postCard.relatedObject.status.unknown', 'Unknown'),
    );
    const typeLabel = t(
        resolveTypeLabelKey(normalizedObjectType),
        normalizedObjectType === 'SESSION' ? 'Session' : 'Event',
    );
    const scheduleTimezone = activeDetails?.timezone || undefined;
    const startsLabel = activeDetails?.startAt
        ? formatDateTime(activeDetails.startAt, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: scheduleTimezone,
        })
        : t('home.postCard.relatedObject.detail.unknownTime', 'Unknown');
    const endsLabel = activeDetails?.endAt
        ? formatDateTime(activeDetails.endAt, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: scheduleTimezone,
        })
        : t('home.postCard.relatedObject.detail.unknownTime', 'Unknown');
    const updatedAtLabel = activeDetails?.updatedAt
        ? formatDateTime(activeDetails.updatedAt)
        : null;
    const address = buildAddress(activeDetails);
    const venueInactive = activeDetails?.venue?.inactive === true;

    const statusVariant = resolveStatusVariant(activeDetails?.status);
    const joinPolicyValue = activeAttendance?.joinPolicy || activeDetails?.joinPolicy;
    const joinPolicyLabel = formatJoinPolicyLabel(t, joinPolicyValue);
    const canOpenEdit = canEdit
        && typeof onEdit === 'function'
        && detailState.source === 'detail'
        && hasEditableOrganizerDetails(activeDetails);
    const attendanceSummaryLabel = t('home.postCard.relatedObject.attendanceSummary', {
        going: attendanceSummary.going,
        interested: attendanceSummary.interested,
        pending: attendanceSummary.pending,
        defaultValue: '{{going}} going · {{interested}} interested · {{pending}} pending',
    });
    const viewerStatusLabel = formatViewerStatusLabel(t, viewerStatus);

    // Render into the global #portal so the fixed overlay escapes the page's
    // stacking context (#content children sit below the fixed mobile header,
    // z-index 50) and paints above the header instead of behind it.
    const portalTarget = typeof document !== 'undefined'
        ? document.getElementById('portal')
        : null;

    const overlay = (
        <PopupOverlay onClose={onClose} closeOnOverlayClick closeOnEsc zIndex={7000}>
            <section className="scheduled-entity-detail-modal" aria-label={t('home.postCard.relatedObject.detail.aria', 'Event details')}>
                <div className="scheduled-entity-detail-modal__hero">
                    <div className="scheduled-entity-detail-modal__hero-overlay" />
                    <div className="scheduled-entity-detail-modal__hero-top">
                        <CloseButton
                            className="scheduled-entity-detail-modal__close"
                            onClick={onClose}
                            ariaLabel={t('home.postCard.relatedObject.detail.close', 'Close details')}
                        />
                    </div>
                    <div className="scheduled-entity-detail-modal__hero-bottom">
                        <div className="scheduled-entity-detail-modal__chips">
                            <span className="scheduled-entity-detail-modal__chip scheduled-entity-detail-modal__chip--type">{typeLabel}</span>
                            <span
                                className={`scheduled-entity-detail-modal__chip scheduled-entity-detail-modal__chip--status scheduled-entity-detail-modal__chip--${statusVariant}`}
                            >
                                {statusLabel}
                            </span>
                            {venueInactive ? (
                                <span className="scheduled-entity-detail-modal__chip scheduled-entity-detail-modal__chip--venue-inactive">
                                    {t('home.postCard.relatedObject.detail.venueInactive', 'Inactive venue')}
                                </span>
                            ) : null}
                        </div>
                        <h3 className="scheduled-entity-detail-modal__title">{title}</h3>
                    </div>
                </div>

                <div className="scheduled-entity-detail-modal__body">
                    <div className="scheduled-entity-detail-modal__info-grid">
                        <div className="scheduled-entity-detail-modal__info-cell">
                            <div className="scheduled-entity-detail-modal__info-icon">{ICONS.calendar}</div>
                            <div className="scheduled-entity-detail-modal__info-text">
                                <span className="scheduled-entity-detail-modal__info-key">
                                    {t('home.postCard.relatedObject.detail.dateTime', 'Date & time')}
                                </span>
                                <p className="scheduled-entity-detail-modal__info-value">{startsLabel}</p>
                                <span className="scheduled-entity-detail-modal__info-sub">
                                    {t('home.postCard.relatedObject.detail.endsAtShort', {
                                        value: endsLabel,
                                        defaultValue: 'Ends {{value}}',
                                    })}
                                </span>
                            </div>
                        </div>

                        <div className="scheduled-entity-detail-modal__info-cell">
                            <div className="scheduled-entity-detail-modal__info-icon">{ICONS.pin}</div>
                            <div className="scheduled-entity-detail-modal__info-text">
                                <span className="scheduled-entity-detail-modal__info-key">
                                    {t('home.postCard.relatedObject.detail.location', 'Location')}
                                </span>
                                <p className="scheduled-entity-detail-modal__info-value">
                                    {address || t('home.postCard.relatedObject.detail.locationUnknown', 'Not specified')}
                                </p>
                            </div>
                        </div>

                        <div className="scheduled-entity-detail-modal__info-cell">
                            <div className="scheduled-entity-detail-modal__info-icon">{ICONS.lock}</div>
                            <div className="scheduled-entity-detail-modal__info-text">
                                <span className="scheduled-entity-detail-modal__info-key">
                                    {t('home.postCard.relatedObject.detail.access', 'Access')}
                                </span>
                                <p className="scheduled-entity-detail-modal__info-value">{joinPolicyLabel}</p>
                                <span className="scheduled-entity-detail-modal__info-sub">{viewerStatusLabel}</span>
                            </div>
                        </div>

                        <div className="scheduled-entity-detail-modal__info-cell">
                            <div className="scheduled-entity-detail-modal__info-icon">{ICONS.users}</div>
                            <div className="scheduled-entity-detail-modal__info-text">
                                <span className="scheduled-entity-detail-modal__info-key">
                                    {t('home.postCard.relatedObject.detail.attendance', 'Attendance')}
                                </span>
                                <p className="scheduled-entity-detail-modal__info-value">{attendanceSummaryLabel}</p>
                                {updatedAtLabel ? (
                                    <span className="scheduled-entity-detail-modal__info-sub">
                                        {t('home.postCard.relatedObject.detail.updatedAtShort', {
                                            value: updatedAtLabel,
                                            defaultValue: 'Updated {{value}}',
                                        })}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    {activeDetails?.description ? (
                        <p className="scheduled-entity-detail-modal__description">{activeDetails.description}</p>
                    ) : null}

                    <div className="scheduled-entity-detail-modal__actions" role="group" aria-label={t('home.postCard.relatedObject.detail.rsvpActions', 'RSVP actions')}>
                        {RSVP_BUTTONS.map(({ status, translationKey }) => (
                            <button
                                key={status}
                                type="button"
                                className={`scheduled-entity-detail-modal__rsvp-btn scheduled-entity-detail-modal__rsvp-btn--${status.toLowerCase()}${activeViewerStatus === status ? ' is-active' : ''}`}
                                onClick={() => handleRsvp(status)}
                                disabled={!canMutateAttendance || isSubmitting}
                            >
                                {t(translationKey, status)}
                            </button>
                        ))}
                    </div>

                    {!readOnly && !canMutateAttendance && (
                        <p className="scheduled-entity-detail-modal__hint">
                            {t('home.postCard.relatedObject.detail.rsvpDisabled', 'RSVP is available only for published events and sessions.')}
                        </p>
                    )}
                    {isLoading && (
                        <p className="scheduled-entity-detail-modal__hint">
                            {t('home.postCard.relatedObject.detail.loading', 'Loading details…')}
                        </p>
                    )}
                    {errorMessage && (
                        <p className="scheduled-entity-detail-modal__error" role="alert">
                            {errorMessage}
                        </p>
                    )}

                    {canOpenEdit ? (
                        <div className="scheduled-entity-detail-modal__owner-actions">
                            <button
                                type="button"
                                className="scheduled-entity-detail-modal__edit-button"
                                onClick={() => onEdit(activeDetails)}
                            >
                                {t('eventsPage.actions.edit', 'Edit')}
                            </button>
                        </div>
                    ) : null}
                </div>
            </section>
        </PopupOverlay>
    );

    return portalTarget ? createPortal(overlay, portalTarget) : overlay;
}

ScheduledEntityDetailModal.propTypes = {
    isOpen: PropTypes.bool,
    objectType: PropTypes.string,
    objectId: PropTypes.string,
    initialSummary: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        status: PropTypes.string,
        visibility: PropTypes.string,
        timezone: PropTypes.string,
        startAt: PropTypes.string,
        endAt: PropTypes.string,
        joinPolicy: PropTypes.string,
        attendeeLimit: PropTypes.number,
        organizerInviteCount: PropTypes.number,
        viewerInvitedByOrganizer: PropTypes.bool,
        viewerAttendanceStatus: PropTypes.string,
        attendanceSummary: PropTypes.shape({
            going: PropTypes.number,
            interested: PropTypes.number,
            pending: PropTypes.number,
        }),
        location: PropTypes.shape({
            locationName: PropTypes.string,
            addressLine: PropTypes.string,
            city: PropTypes.string,
            stateOrRegion: PropTypes.string,
            country: PropTypes.string,
            countryCode: PropTypes.string,
            latitude: PropTypes.number,
            longitude: PropTypes.number,
        }),
        locationName: PropTypes.string,
        addressLine: PropTypes.string,
        city: PropTypes.string,
        stateOrRegion: PropTypes.string,
        country: PropTypes.string,
        countryCode: PropTypes.string,
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        organizer: PropTypes.shape({
            objectType: PropTypes.string,
            objectId: PropTypes.string,
            name: PropTypes.string,
        }),
        venue: PropTypes.shape({
            objectId: PropTypes.string,
            name: PropTypes.string,
            sourceType: PropTypes.string,
            inactive: PropTypes.bool,
            inactiveAt: PropTypes.string,
        }),
        updatedAt: PropTypes.string,
        createdAt: PropTypes.string,
    }),
    initialAttendance: PropTypes.shape({
        id: PropTypes.string,
        objectType: PropTypes.string,
        scheduledStatus: PropTypes.string,
        joinPolicy: PropTypes.string,
        viewerAttendanceStatus: PropTypes.string,
        viewerRespondedAt: PropTypes.string,
        attendanceSummary: PropTypes.shape({
            going: PropTypes.number,
            interested: PropTypes.number,
            pending: PropTypes.number,
        }),
    }),
    onClose: PropTypes.func,
    onAttendanceChange: PropTypes.func,
    canEdit: PropTypes.bool,
    onEdit: PropTypes.func,
    readOnly: PropTypes.bool,
};
